import React, { useState, useEffect } from 'react';
import { handleApiError } from '../utils/errorHandler';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const AdminPanel = () => {
  const [toilets, setToilets] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingToilet, setEditingToilet] = useState(null);
  const [loading, setLoading] = useState(true);
  const { isAdmin } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    lat: '',
    lng: '',
    hours: '',
    accessible: false,
    cleanliness_rating: '',
    notes: ''
  });

  useEffect(() => {
    if (isAdmin) {
      fetchToilets();
    }
  }, [isAdmin]);

  const fetchToilets = async () => {
    try {
      const response = await axios.get('http://localhost:5000/api/admin/toilets');
      setToilets(response.data);
    } catch (error) {
      handleApiError(error, 'Failed to fetch toilets');
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...formData,
        lat: parseFloat(formData.lat),
        lng: parseFloat(formData.lng),
        cleanliness_rating: formData.cleanliness_rating ? parseFloat(formData.cleanliness_rating) : null
      };

      if (editingToilet) {
        await axios.put(`http://localhost:5000/api/admin/toilets/${editingToilet.id}`, payload);
      } else {
        await axios.post('http://localhost:5000/api/admin/toilets', payload);
      }
      
      resetForm();
      fetchToilets();
    } catch (error) {
      handleApiError(error, 'Failed to save toilet');
    }
  };

  const handleEdit = (toilet) => {
    setEditingToilet(toilet);
    setFormData({
      name: toilet.name,
      address: toilet.address || '',
      lat: toilet.lat.toString(),
      lng: toilet.lng.toString(),
      hours: toilet.hours || '',
      accessible: toilet.accessible,
      cleanliness_rating: toilet.cleanliness_rating ? toilet.cleanliness_rating.toString() : '',
      notes: toilet.notes || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this toilet location?')) {
      try {
        await axios.delete(`http://localhost:5000/api/admin/toilets/${id}`);
        fetchToilets();
      } catch (error) {
        handleApiError(error, 'Failed to delete toilet');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      address: '',
      lat: '',
      lng: '',
      hours: '',
      accessible: false,
      cleanliness_rating: '',
      notes: ''
    });
    setEditingToilet(null);
    setShowForm(false);
  };

  if (!isAdmin) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-red-600">Access Denied</h2>
        <p className="text-gray-600 mt-2">This page is only accessible to administrators.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="backdrop-blur-sm bg-white/80 rounded-3xl shadow-2xl border border-white/20 p-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">🔧 Admin Panel - Toilet Locations</h1>
          <button
            onClick={() => setShowForm(true)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            Add New Location
          </button>
        </div>

        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="backdrop-blur-sm bg-white/90 rounded-3xl shadow-2xl border border-white/20 p-8 max-w-2xl w-full max-h-screen overflow-y-auto">
              <h2 className="text-2xl font-bold mb-6">
                {editingToilet ? 'Edit Toilet Location' : 'Add New Toilet Location'}
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white/50 backdrop-blur-sm"
                      placeholder="e.g., Central Park Public Restroom"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Address
                    </label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white/50 backdrop-blur-sm"
                      placeholder="Full address"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Latitude *
                    </label>
                    <input
                      type="number"
                      step="any"
                      required
                      value={formData.lat}
                      onChange={(e) => setFormData({ ...formData, lat: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white/50 backdrop-blur-sm"
                      placeholder="40.7128"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Longitude *
                    </label>
                    <input
                      type="number"
                      step="any"
                      required
                      value={formData.lng}
                      onChange={(e) => setFormData({ ...formData, lng: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white/50 backdrop-blur-sm"
                      placeholder="-74.0060"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Hours
                    </label>
                    <input
                      type="text"
                      value={formData.hours}
                      onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white/50 backdrop-blur-sm"
                      placeholder="e.g., 6:00 AM - 10:00 PM"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Cleanliness Rating (1-5)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="5"
                      step="0.1"
                      value={formData.cleanliness_rating}
                      onChange={(e) => setFormData({ ...formData, cleanliness_rating: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white/50 backdrop-blur-sm"
                      placeholder="3.5"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.accessible}
                        onChange={(e) => setFormData({ ...formData, accessible: e.target.checked })}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="text-sm font-medium text-gray-700">Wheelchair Accessible</span>
                    </label>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Notes
                    </label>
                    <textarea
                      rows={3}
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white/50 backdrop-blur-sm"
                      placeholder="Additional information..."
                    />
                  </div>
                </div>

                <div className="flex space-x-4 pt-4">
                  <button
                    type="submit"
                    className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white py-3 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    {editingToilet ? 'Update Location' : 'Add Location'}
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-3 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="grid gap-4">
          {toilets.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🚻</div>
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No toilet locations</h3>
              <p className="text-gray-500">Click "Add New Location" to add the first toilet location.</p>
            </div>
          ) : (
            toilets.map((toilet) => (
              <div key={toilet.id} className="backdrop-blur-sm bg-white/60 rounded-2xl border border-white/30 p-6 shadow-lg hover:shadow-xl transition-all duration-200">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-3">
                      <h3 className="text-xl font-semibold text-gray-900">{toilet.name}</h3>
                      {toilet.accessible && (
                        <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                          ♿ Accessible
                        </span>
                      )}
                      {toilet.cleanliness_rating && (
                        <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                          ⭐ {toilet.cleanliness_rating}/5
                        </span>
                      )}
                    </div>
                    
                    <div className="space-y-2 text-gray-600">
                      {toilet.address && (
                        <p className="flex items-center">
                          <span className="font-medium text-gray-700">📍 Address:</span>
                          <span className="ml-2">{toilet.address}</span>
                        </p>
                      )}
                      
                      <p className="flex items-center">
                        <span className="font-medium text-gray-700">🗺️ Coordinates:</span>
                        <span className="ml-2">{toilet.lat}, {toilet.lng}</span>
                      </p>
                      
                      {toilet.hours && (
                        <p className="flex items-center">
                          <span className="font-medium text-gray-700">🕒 Hours:</span>
                          <span className="ml-2">{toilet.hours}</span>
                        </p>
                      )}
                      
                      {toilet.notes && (
                        <p className="flex items-start">
                          <span className="font-medium text-gray-700">📝 Notes:</span>
                          <span className="ml-2">{toilet.notes}</span>
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex space-x-2 ml-4">
                    <button
                      onClick={() => handleEdit(toilet)}
                      className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-lg transition-colors duration-200"
                      title="Edit location"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleDelete(toilet.id)}
                      className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-lg transition-colors duration-200"
                      title="Delete location"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;